"use client";

import IconPlus from "@/components/icons/icon-plus";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

const tabs = [
  { name: "Reservations & Visits", content: "Reservations & Visits" },
  { name: "Menu & Dietary", content: "Menu & Dietary" },
  { name: "Events & Celebrations", content: "Events & Celebrations" },
  { name: "General Info", content: "General Info" },
];

const contents = [
  {
    name: "Reservations & Visits",
    faqs: [
      {
        question: "What type of cuisine do you serve?",
        answer:
          "We serve authentic Arabian cuisine, prepared by expert Yemeni chefs using traditional recipes and premium ingredients.",
      },
      {
        question: "Is Al Majlis family-friendly?",
        answer:
          "Yes. Al Majlis is a family-friendly restaurant with a comfortable and welcoming atmosphere for guests of all ages",
      },
      {
        question: "Do you provide takeaway or delivery?",
        answer:
          "Yes, we offer both takeaway and delivery services for your convenience.",
      },
      {
        question: "Do you have vegetarian or vegan options?",
        answer:
          "Yes, we offer a selection of vegetarian options. Please ask our staff for available choices.",
      },
      {
        question: "What are your opening hours?",
        answer:
          "We are open daily from *11:00 AM - 2:00 AM",
      },
    ],
  },

  {
    name: "Menu & Dietary",
    faqs: [
       {
        question: "What type of cuisine do you serve?",
        answer:
          "We serve authentic Arabian cuisine, prepared by expert Yemeni chefs using traditional recipes and premium ingredients.",
      },
      {
        question: "Is Al Majlis family-friendly?",
        answer:
          "Yes. Al Majlis is a family-friendly restaurant with a comfortable and welcoming atmosphere for guests of all ages",
      },
    ],
  },
  {
    name: "Events & Celebrations",
    faqs: [
    {
        question: "What type of cuisine do you serve?",
        answer:
          "We serve authentic Arabian cuisine, prepared by expert Yemeni chefs using traditional recipes and premium ingredients.",
      },
      {
        question: "Is Al Majlis family-friendly?",
        answer:
          "Yes. Al Majlis is a family-friendly restaurant with a comfortable and welcoming atmosphere for guests of all ages",
      },
      {
        question: "Do you provide takeaway or delivery?",
        answer:
          "Yes, we offer both takeaway and delivery services for your convenience.",
      },
       {
        question: "Do you have vegetarian or vegan options?",
        answer:
          "Yes, we offer a selection of vegetarian options. Please ask our staff for available choices.",
      },
      {
        question: "What are your opening hours?",
        answer:
            "We are open daily from *11:00 AM - 2:00 AM",
        },
    ],
  },
  {
    name: "General Info",
    faqs: [
      {
        question: "What type of cuisine do you serve?",
        answer:
          "We serve authentic Arabian cuisine, prepared by expert Yemeni chefs using traditional recipes and premium ingredients.",
      },
      {
        question: "Is Al Majlis family-friendly?",
        answer:
          "Yes. Al Majlis is a family-friendly restaurant with a comfortable and welcoming atmosphere for guests of all ages",
      },
      {
        question: "Do you provide takeaway or delivery?",
        answer:
          "Yes, we offer both takeaway and delivery services for your convenience.",
      },
      {
        question: "Do you have vegetarian or vegan options?",
        answer:
          "Yes, we offer a selection of vegetarian options. Please ask our staff for available choices.",
      },
      {
        question: "What are your opening hours?",
        answer:
          "We are open daily from *11:00 AM - 2:00 AM",
      },
    ],
  },
];

const FaqSection = () => {
  const [selectedFaq, setSelectedFaq] = useState<string | null>(null);

  const toggleFaq = (question: string) => {
    setSelectedFaq((prev) => (prev === question ? null : question));
  };

  return (
    <section className="py-7.5 lg:py-20 px-7.5" id="faq">
      <div className="text-center">
        <h1 className="text-[33px] sm:text-4xl xl:text-[70px] leading-[90%] xl:leading-[73px] text-old-lace font-amiri font-bold mb-2">
          Good to Know Before You Visit
        </h1>
        <p className="text-sm sm:text-base xl:text-[28px] leading-[110%] font-medium">
          From Reservations to Special Dishes — We’ve Got You Covered
        </p>
      </div>

      <div>
        {/* Faq Tabs */}
        <Tabs defaultValue={contents[0].name} className="center">
          <TabsList className="w-full h-auto flex flex-wrap items-center justify-center gap-2.5 my-6 2xl:mt-8 2xl:mb-16">
            {tabs.map((tab) => (
              <TabsTrigger
                value={tab.name}
                key={tab.name}
                className={cn(
                  "grow-0 px-3.75 sm:px-3.75 py-1.75 sm:py-2 text-[13px] rounded-[10px] bg-white text-[#573E69]",
                  "data-[state=active]:bg-old-gold data-[state=active]:text-white data-[state=active]:font-medium"
                )}
              >
                {tab.name}
              </TabsTrigger>
            ))}
          </TabsList>

          {contents.map((tab) => (
            <TabsContent
              value={tab.name}
              key={tab.name}
              className="max-w-[1122.66px] w-full mx-auto"
            >
              <div className="w-full flex flex-col gap-5.5">
                {tab.faqs.map((faq) => (
                  <div
                    key={faq.question}
                    className="border border-white/[50%] px-[22px] py-[25px] sm:p-7 xl:py-6 xl:px-9.5 rounded-[16.84px] cursor-pointer"
                    onClick={() => toggleFaq(faq.question)}
                  >
                    <div className="flex flex-col lg:flex-row justify-between lg:items-center">
                      <div className="w-full flex items-center justify-between">
                        <h3 className="md:max-w-[400px] lg:max-w-[471.52px] lg:w-full shrink-0 text-[14.56px] sm:text-xl xl:text-[25px] leading-[30px] font-normal text-old-lace">
                          {faq.question}
                        </h3>
                        <IconPlus
                          className={cn(
                            "shrink-0 transition-transform duration-300 lg:hidden block w-5",
                            selectedFaq === faq.question
                              ? "rotate-45"
                              : "rotate-0"
                          )}
                        />
                      </div>
                      <AnimatePresence>
                        {selectedFaq === faq.question && (
                          <motion.div
                            key={faq.question}
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.4, ease: "easeInOut" }}
                            className="overflow-hidden lg:px-4 2xl:px-7"
                          >
                            <p className="text-[13px] sm:text-lg xl:text-[22.45px] leading-[130%] xl:leading-[31.45px] pt-4 lg:pt-5 text-old-lace">
                              {faq.answer}
                            </p>
                          </motion.div>
                        )}
                      </AnimatePresence>{" "}
                      <IconPlus
                        className={cn(
                          "shrink-0 transition-transform duration-300 lg:block hidden",
                          selectedFaq === faq.question
                            ? "rotate-45"
                            : "rotate-0"
                        )}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </section>
  );
};

export default FaqSection;
